import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LoaderWithProgress = ({ loadingText }) => {
  // Auto-incrementing progress
  const [autoProgress, setAutoProgress] = useState(0);
  // Manual progress for demonstration
  const [manualProgress, setManualProgress] = useState(45);

  const { t } = useTranslation("general");

  // Simulate auto-incrementing progress
  useEffect(() => {
    const timer = setInterval(() => {
      setAutoProgress((prev) => {
        const increment =
          prev < 30 ? 1 : prev < 60 ? 0.8 : prev < 85 ? 0.5 : 0.2;
        const newProgress = prev + increment;
        return newProgress >= 90 ? 90 : newProgress;
      });
    }, 300); // Sped up for demo

    return () => clearInterval(timer);
  }, []);

  // Container styles
  const containerStyle = {
    padding: "1rem",
    fontFamily: "sans-serif",
    maxWidth: "800px",
    margin: "0 auto",
  };

  // Circular loader component
  const CircularLoader = ({ progress, size, loadingText }) => {
    // Size configuration
    const sizeConfig = {
      xsmall: { width: 30, height: 30, strokeWidth: 3, fontSize: 8 },
      small: { width: 45, height: 45, strokeWidth: 4, fontSize: 12 },
      medium: { width: 60, height: 60, strokeWidth: 5, fontSize: 14 },
      large: { width: 75, height: 75, strokeWidth: 6, fontSize: 18 },
    };

    const { width, height, strokeWidth, fontSize } =
      sizeConfig[size] || sizeConfig.medium;

    // SVG calculation
    const radius = width / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (progress / 100) * circumference;

    const containerStyle = {
      position: "relative",
      width: `${width}px`,
      height: `${height}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };

    const svgStyle = {
      transform: "rotate(-90deg)",
      width: "100%",
      height: "100%",
    };

    const backgroundCircleStyle = {
      fill: "transparent",
      stroke: "#f0f0f0",
      strokeWidth: `${strokeWidth}px`,
    };

    const progressCircleStyle = {
      fill: "transparent",
      stroke: "#79329a",
      strokeWidth: `${strokeWidth}px`,
      strokeLinecap: "round",
      transition: "stroke-dashoffset 0.3s ease",
      strokeDasharray: `${circumference} ${circumference}`,
      strokeDashoffset: dashOffset,
    };

    const textStyle = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: `${fontSize}px`,
      fontWeight: 600,
      color: "#333",
    };

    const loadingTextStyle = {
      fontSize: "0.875rem",
      color: "#666",
      margin: "1rem 0 0",
      textAlign: "center",
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={containerStyle}>
          <svg style={svgStyle} viewBox={`0 0 ${width} ${height}`}>
            <circle
              style={backgroundCircleStyle}
              cx={width / 2}
              cy={height / 2}
              r={radius}
            />
            <circle
              style={progressCircleStyle}
              cx={width / 2}
              cy={height / 2}
              r={radius}
            />
          </svg>
          <div style={textStyle}>{Math.round(progress)}%</div>
        </div>
        <p style={loadingTextStyle}>{t(loadingText || "loading")}</p>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <CircularLoader
        progress={autoProgress}
        size="large"
        loadingText={loadingText}
      />
    </div>
  );
};

export default LoaderWithProgress;
